import styled from 'styled-components';
import React from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';

import { client, TremfyaExpressEnrollmentResponse } from '../api';
import { GeneralInfoTable } from '../components/GeneralInfoTable/GeneralInfoTable';
import TremfyaExpressEnrollmentCoreForm from '../components/tasks/tremfyaExpressEnrollmentTask/TremfyaExpressEnrollmentCoreForm';
import { TopNavbar } from '../components/TopNavbar/TopNavbar';
import { PharmaRelationshipNotice } from '../components/PharmaRelationshipNotice/PharmaRelationshipNotice';

const Container = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100%;
  background-color: var(--light-surface);
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const BodyContainer = styled.div`
  box-sizing: border-box;
  max-width: var(--main-content-width);
  padding: 20px;
  margin: 0 auto;
  background-color: var(--white);
  border: 1px solid var(--border-gray);
  border-radius: var(--border-radius-small);
`;

const StyledPharmaRelationshipNotice = styled(PharmaRelationshipNotice)`
  margin-bottom: 16px;
`;

/**
 * Version of TremfyaExpressEnrollmentTaskView that is used for logged out users.
 *
 * This is used by practices that don't use the partner portal for tasking and instead use an
 * alternative like EMA. They will receive a message in their tasking system with a link to this
 * page, which they will view while (probably) logged out of the partner portal.
 */
const TremfyaExpressEnrollmentLoggedOutTaskPage: React.FC = () => {
  const params = useParams();
  const token = params.token!;

  const queryClient = useQueryClient();
  const tremfyaExpressEnrollmentTaskQuery = useQuery<TremfyaExpressEnrollmentResponse>({
    queryKey: ['tremfya-enrollment', token],
    queryFn: async () => {
      const res = await client.get(`tremfya-enrollment/${token}`);
      return res.data;
    },
  });

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['tremfya-enrollment', token],
    });
    toast.custom(() => <Toast variant="success">Enrollment submitted</Toast>);
  };

  let body;
  if (tremfyaExpressEnrollmentTaskQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (tremfyaExpressEnrollmentTaskQuery.error) {
    body = <PageLoadError />;
  } else if (tremfyaExpressEnrollmentTaskQuery.data) {
    const { form_data, drug, status } = tremfyaExpressEnrollmentTaskQuery.data;

    body = (
      <BodyContainer>
        <StyledPharmaRelationshipNotice variant="banner" />

        <Typography styledAs="h6" marginBottom="1rem">
          Tremfya Express Enrollment
        </Typography>

        <GeneralInfoTable
          patientName={drug.patient_name}
          prescriberName={drug.prescriber_name}
          dob={drug.dob}
          prescribedOn={drug.prescription_recieved_at}
          drugName={drug.name}
          prescriptionId={drug.id}
        />

        {status !== 'draft' ? (
          <Typography styledAs="bodySmallDMSans">This enrollment has been completed.</Typography>
        ) : (
          <TremfyaExpressEnrollmentCoreForm
            drug={drug}
            formData={form_data}
            submissionEndpoint={`tremfya-enrollment/${token}/complete`}
            onSuccess={onSuccess}
          />
        )}
      </BodyContainer>
    );
  }

  return (
    <Container>
      <TopNavbar />
      {body}
    </Container>
  );
};

export default TremfyaExpressEnrollmentLoggedOutTaskPage;
